import { Tabs, type TabsTabProps } from "@mantine/core";
import { useFieldArray, useForm } from "react-hook-form";
import { useMatch, useNavigate, useResolvedPath } from "react-router";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { isApiError } from "../../../../../utils/isApiError";
import { buildFieldNameToLabelMap } from "../../../../../utils/react-hook-form/buildFieldNameToLabelMap";
import { useDeliveries } from "../../../../DeliveryInvoices/hooks/useDeliveries";
import { HistoryTab } from "../../../../HistoryTab/HistoryTab";
import {
  InstallmentCalculationBase,
  PaymentIntervalAndBillingMethod,
  type Contract,
  type InstallmentConfiguration,
  type Payment
} from "../../../DeliveryOverview.types";
import { CONTRACT_FORM_FIELD_DATA } from "../ContractForm/ContractForm.constants";
import { ContractFormControls } from "../ContractForm/ContractFormControls/ContractFormControls";
import { ContractFormFields } from "../ContractForm/ContractFormFields";
import { useContractSubmit } from "../ContractForm/useContractSubmit";
import type { ContractFormOptions } from "../ContractModal.types";
import { InstallmentsTab } from "../Tabs/InstallmentsTab/InstallmentsTab";
import { PaymentsTab } from "../Tabs/PaymentsTab";

type TabProps = TabsTabProps & { label: string };

const CONTRACT_TABS = {
  TAB_GENERAL: "allgemein",
  TAB_INSTALLMENTS: "abschlaege",
  TAB_PAYMENTS: "aufschlaege",
  TAB_HISTORY: "historie"
};

const TABS: Array<TabProps> = [
  {
    value: CONTRACT_TABS.TAB_GENERAL,
    label: "Allgemein"
  },
  {
    value: CONTRACT_TABS.TAB_INSTALLMENTS,
    label: "Abschläge"
  },
  {
    value: CONTRACT_TABS.TAB_PAYMENTS,
    label: "Sonstige Zahlungen"
  },
  {
    value: CONTRACT_TABS.TAB_HISTORY,
    label: "Historie"
  }
];

interface EditFormProps {
  contract: Contract;
  options: ContractFormOptions;
  projectId: string;
  buttonContainer?: HTMLElement;
  missingFields: Array<string>;
  shouldFieldBeHighlighted: (
    fieldName: string,
    relevantFormValues: Record<string, unknown>
  ) => boolean;
  onSubmit: (contract: Contract) => Promise<void>;
  onCancel: () => void;
}

function EditForm({
  contract,
  options,
  projectId,
  buttonContainer,
  missingFields,
  shouldFieldBeHighlighted,
  onSubmit,
  onCancel
}: EditFormProps) {
  const {
    clearErrors,
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch
  } = useForm<Contract>({ defaultValues: contract });

  const installments = watch("installment_configurations");
  const { fields: paymentFields, prepend: prependPayments } = useFieldArray({
    control,
    name: "payments"
  });

  const isMonthlyBilled =
    contract.billing_method_and_period ===
    PaymentIntervalAndBillingMethod.MonthlyBasedOnMeasurements;

  const filteredTabs = TABS.filter(
    (tab) =>
      tab.value !== CONTRACT_TABS.TAB_INSTALLMENTS ||
      contract.billing_method_and_period ===
        PaymentIntervalAndBillingMethod.MonthlyAdvancePaymentsAndAnnualFinalInvoice
  );
  const navigate = useNavigate();
  const resolved = useResolvedPath(":activeTab");
  const match = useMatch({ path: resolved.pathname, end: true });
  const activeTab = match?.params.activeTab ?? CONTRACT_TABS.TAB_GENERAL;
  const fieldNameToLabelMap = buildFieldNameToLabelMap<Contract>(
    CONTRACT_FORM_FIELD_DATA
  );

  const { deliveries } = useDeliveries(projectId);

  function updateInstallments(
    newInstallments: Array<InstallmentConfiguration>
  ) {
    setValue("installment_configurations", newInstallments);
  }

  function addInstallment(): InstallmentConfiguration {
    const currentInstallments = getValues("installment_configurations");
    let yearToAdd = new Date().getFullYear();
    if (currentInstallments) {
      const latestYear = [...currentInstallments].sort(
        (a, b) => b.year - a.year
      )[0]?.year;

      yearToAdd = latestYear ? latestYear + 1 : yearToAdd;
    }
    const newInstallment: InstallmentConfiguration = {
      year: yearToAdd,
      calculation_base: InstallmentCalculationBase.HistoricalMeasurementValues,
      estimated_delivery_amount_in_kwh: null,
      estimated_share_direct_delivery_in_percent: null,
      installment_payments_made: null,
      user_requested_installment_in_euro: null,
      estimated_installment_in_euro: null
    };
    setValue(
      "installment_configurations",
      currentInstallments
        ? [newInstallment, ...currentInstallments]
        : [newInstallment]
    );

    return newInstallment;
  }

  function addPayment(hasRlmDeliveries: boolean) {
    const currentPayments = getValues("payments");
    if (currentPayments) {
      const contractBegin = backendDateOrDateTimeToLuxonDateTime(
        contract.begin
      );
      const now = new Date();

      const previousYearOrContractBeginning = Math.max(
        contractBegin.year,
        now.getFullYear() - 1
      );

      const previousMonthOrContractBeginning =
        contractBegin.toJSDate() > now
          ? contractBegin.month
          : (now.getMonth() - 1) % 12;

      const latestYear: number =
        [...currentPayments].sort((a, b) => b.year - a.year)[0]?.year ||
        previousYearOrContractBeginning - 1;

      const latestMonth: number =
        currentPayments
          .filter((pd) => pd.year === latestYear)
          .sort((a, b) => (!!a.month && !!b.month ? b.month - a.month : 0))[0]
          ?.month ||
        (currentPayments
          .filter((pd) => pd.year === latestYear)
          .find((pd) => pd.month === null) !== undefined
          ? 12
          : previousMonthOrContractBeginning) ||
        previousMonthOrContractBeginning;

      /* if yearly based: only rows for years (month null) */
      /* if monthly based with rlm delivery: rows for months + row for year (no month) */
      /* if monthly based without rlm delivery: only rows for months */
      const newPayment: Payment = {
        year: isMonthlyBilled
          ? latestMonth === 12
            ? latestYear + 1
            : latestYear
          : latestYear + 1,
        month: isMonthlyBilled
          ? latestMonth === 12
            ? 1
            : latestMonth + 1
          : null,
        pending_payments: null,
        pending_payments_description: null,
        discount_payments: null,
        discount_description: null
      };

      if (isMonthlyBilled && hasRlmDeliveries && latestMonth + 1 === 12) {
        const newExtraPayment: Payment = {
          year: latestYear,
          month: null,
          pending_payments: null,
          pending_payments_description: null,
          discount_payments: null,
          discount_description: null
        };
        prependPayments([newExtraPayment, newPayment]);
      } else {
        prependPayments(newPayment);
      }
    }
  }

  const onValidSubmit = useContractSubmit({
    onSubmit,
    watch,
    setError,
    onError: (error) => {
      if (
        isApiError<Record<keyof Contract, Array<string>>>(error) &&
        error.response?.data
      ) {
        if (error.response.data.installment_configurations) {
          navigate(`./${CONTRACT_TABS.TAB_INSTALLMENTS}`);
        } else if (error.response.data.payments) {
          navigate(`./${CONTRACT_TABS.TAB_PAYMENTS}`);
        } else {
          navigate(`./${CONTRACT_TABS.TAB_GENERAL}`);
        }
      }
    }
  });

  const submitCallback = handleSubmit(onValidSubmit);

  function handleSubmitForm() {
    // API errors can land on paths without a registered field (e.g.
    // `installment_configurations.0.…`), and `handleSubmit` only unsets errors
    // of registered fields. Left in place they keep every following submit on
    // the invalid branch.
    clearErrors(["payments", "installment_configurations"]);

    return submitCallback();
  }

  function handleClickCancel() {
    reset();
    onCancel();
  }

  return (
    <div className="EditForm">
      <Tabs value={activeTab} onChange={(newTab) => navigate(`./${newTab}`)}>
        <Tabs.List>
          {filteredTabs.map((tab) => (
            <Tabs.Tab component="a" key={tab.value} value={tab.value}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <Tabs.Panel value={CONTRACT_TABS.TAB_GENERAL}>
          <div className="ContractForm FormItems">
            <ContractFormFields
              contract={contract}
              control={control}
              errors={errors}
              missingFields={missingFields}
              options={options}
              reset={reset}
              setValue={setValue}
              shouldFieldBeHighlighted={shouldFieldBeHighlighted}
              watch={watch}
            />
          </div>
        </Tabs.Panel>
        <Tabs.Panel value={CONTRACT_TABS.TAB_INSTALLMENTS}>
          {installments && (
            <InstallmentsTab
              addInstallment={addInstallment}
              beginYear={parseInt(contract.begin.split(".")[2])}
              contractId={contract.id}
              deliveries={deliveries.filter(
                (delivery) => delivery.contract === contract.id
              )}
              installmentsData={installments}
              projectId={projectId}
              updateInstallments={updateInstallments}
            />
          )}
        </Tabs.Panel>
        <Tabs.Panel value={CONTRACT_TABS.TAB_PAYMENTS}>
          <PaymentsTab
            addPayment={addPayment}
            billingMethodAndPeriod={contract.billing_method_and_period}
            control={control}
            deliveries={deliveries.filter(
              (delivery) => delivery.contract === contract.id
            )}
            paymentsData={paymentFields}
          />
        </Tabs.Panel>
        <Tabs.Panel value={CONTRACT_TABS.TAB_HISTORY}>
          <HistoryTab
            entityId={contract.id}
            entityType={"contract"}
            fieldNameToLabelMap={fieldNameToLabelMap}
          />
        </Tabs.Panel>
      </Tabs>
      <ContractFormControls
        buttonContainer={buttonContainer}
        onCancel={handleClickCancel}
        onSubmit={handleSubmitForm}
      />
    </div>
  );
}

export { EditForm, EditFormProps };
